import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Factory, Mail, Phone, Package, Building2, ClipboardList, Calendar } from "lucide-react";

export default function AdminManufacturers() {
  const [mfrs, setMfrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manufacturers");
      setMfrs(data);
    } catch (e) {
      console.error("Failed to load manufacturers:", e);
      toast.error("Failed to load manufacturers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    if (!ql) return mfrs;
    return mfrs.filter((m) =>
      (m.email || "").toLowerCase().includes(ql)
      || (m.company_name || "").toLowerCase().includes(ql)
      || (m.contact_name || "").toLowerCase().includes(ql)
    );
  }, [mfrs, q]);

  const stats = useMemo(() => ({
    total: mfrs.length,
    active: mfrs.filter((m) => m.products_count > 0 || m.dealers_count > 0).length,
    newThisMonth: mfrs.filter((m) => {
      if (!m.created_at) return false;
      const d = new Date(m.created_at);
      const now = new Date();
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length,
  }), [mfrs]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-manufacturers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Admin · Network</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-1 leading-none text-[#1A365D]">
            Manufacturers<span className="text-[#C62828]">.</span>
          </h1>
          <p className="mt-2 text-slate-600">Everyone who has signed up to list products through LumberLoop USA.</p>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-2" data-testid="reload-mfrs">
          <RefreshCw size={16}/> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <StatCard label="Total Manufacturers" value={stats.total} icon={Factory}/>
        <StatCard label="Active (with products/dealers)" value={stats.active} icon={Package}/>
        <StatCard label="New This Month" value={stats.newThisMonth} icon={Calendar}/>
      </div>

      {/* Search */}
      <div className="mt-6 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input
          className="ll-input pl-9"
          placeholder="Search by company, contact, or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="mfr-search-input"
        />
      </div>

      <div className="mt-4 brutal-card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1A365D] text-white uppercase font-mono-ll text-[11px] tracking-widest">
            <tr>
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Signed Up</th>
              <th className="px-4 py-3 text-center">Products</th>
              <th className="px-4 py-3 text-center">Dealers</th>
              <th className="px-4 py-3 text-center">Leads</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">LOADING…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">
                {q ? "NO MATCHES" : "NO MANUFACTURERS HAVE SIGNED UP YET"}
              </td></tr>
            ) : filtered.map((m) => (
              <tr key={m.id} className="border-t-2 border-slate-200 hover:bg-slate-50" data-testid={`mfr-row-${m.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1A365D] text-white flex items-center justify-center flex-shrink-0">
                      <Building2 size={16}/>
                    </div>
                    <div>
                      <div className="font-semibold text-[#1A365D]">{m.company_name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]">{m.contact_name || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 text-xs">
                    <a href={`mailto:${m.email}`} className="flex items-center gap-1 text-[#1A365D] hover:text-[#C62828]">
                      <Mail size={12}/> {m.email}
                    </a>
                    {m.phone && <span className="flex items-center gap-1 text-slate-600"><Phone size={12}/> {m.phone}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-center"><Pill n={m.products_count}/></td>
                <td className="px-4 py-3 text-center"><Pill n={m.dealers_count}/></td>
                <td className="px-4 py-3 text-center"><Pill n={m.leads_received} highlight/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="brutal-card-flat p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-tag">{label}</div>
          <div className="font-heading text-4xl font-black mt-2 text-[#1A365D]">{value}</div>
        </div>
        <div className="w-12 h-12 bg-[#0F2540] text-white flex items-center justify-center">
          <Icon size={20}/>
        </div>
      </div>
    </div>
  );
}

function Pill({ n, highlight = false }) {
  const cls = n === 0
    ? "bg-slate-100 text-slate-500"
    : highlight
      ? "bg-[#C62828] text-white"
      : "bg-[#1A365D] text-white";
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-mono-ll font-bold ${cls}`}>{n}</span>
  );
}
